import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Play, Pause, Loader2, MessageCircle, Send, Download, Radio, Flame, Heart, Repeat2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${BACKEND_URL}/api`;

export default function SetPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);
  const [progress, setProgress] = useState(0);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [snippetBusy, setSnippetBusy] = useState(false);
  const [reactions, setReactions] = useState({ counts: { fire: 0, heart: 0 }, my_reactions: [] });
  const [repost, setRepost] = useState({ count: 0, reposted: false });

  useEffect(() => {
    axios.get(`${API}/sets/${id}`).then(r => setData(r.data)).catch(e => setError(e.response?.data?.detail || "Set not found"));
    axios.get(`${API}/sets/${id}/comments`).then(r => setComments(r.data)).catch(() => {});
    axios.get(`${API}/sets/${id}/reactions`).then(r => setReactions(r.data)).catch(() => {});
    axios.get(`${API}/sets/${id}/reposts`).then(r => setRepost(r.data)).catch(() => {});
  }, [id]);

  const toggleRepost = async () => {
    if (!user) { toast.error("Log in to repost"); return; }
    try {
      if (repost.reposted) {
        await axios.delete(`${API}/sets/${id}/repost`);
        setRepost({ count: Math.max(0, repost.count - 1), reposted: false });
        toast.success("Repost removed");
      } else {
        await axios.post(`${API}/sets/${id}/repost`, { caption: "" });
        setRepost({ count: repost.count + 1, reposted: true });
        toast.success("Reposted to your profile");
      }
    } catch (e) {
      if (e.response?.status === 402) toast.error("Reposts are a PRO feature — upgrade in Studio");
      else toast.error("Repost failed");
    }
  };

  const toggleReaction = async (kind) => {
    if (!user) { toast.error("Log in to react"); return; }
    const already = reactions.my_reactions.includes(kind);
    try {
      const r = already
        ? await axios.delete(`${API}/sets/${id}/reactions/${kind}`)
        : await axios.post(`${API}/sets/${id}/reactions`, { kind });
      setReactions(prev => ({
        counts: r.data.counts,
        my_reactions: already ? prev.my_reactions.filter(k => k !== kind) : [...prev.my_reactions, kind],
      }));
    } catch { toast.error("Reaction failed"); }
  };

  const audioUrl = `${API}/sets/${id}/audio`;

  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch {} }
  };

  const fmt = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const on = () => { if (a.duration) setProgress((a.currentTime / a.duration) * 100); };
    a.addEventListener("timeupdate", on);
    a.addEventListener("ended", () => setPlaying(false));
    return () => a.removeEventListener("timeupdate", on);
  }, [data]);

  const postComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Log in to comment"); return; }
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const at = audioRef.current ? audioRef.current.currentTime : 0;
      const r = await axios.post(`${API}/sets/${id}/comments`, { content: newComment.trim(), at_seconds: at });
      setComments(prev => [...prev, r.data].sort((a, b) => a.at_seconds - b.at_seconds));
      setNewComment("");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Failed to post"); }
    finally { setPosting(false); }
  };

  const seekTo = (t) => {
    if (audioRef.current) { audioRef.current.currentTime = t; }
  };

  const generateSnippet = async () => {
    if (!data || !data.duration_seconds) { toast.error("Set has no audio."); return; }
    setSnippetBusy(true);
    try {
      // 1. Create offscreen 1080x1920 canvas
      const canvas = document.createElement("canvas");
      canvas.width = 1080; canvas.height = 1920;
      const ctx = canvas.getContext("2d");

      // 2. Fetch & decode audio (or reuse a hidden audio element with captureStream)
      const audio = new Audio(audioUrl);
      audio.crossOrigin = "anonymous";
      // Skip to a mid-set point for a punchier snippet
      audio.currentTime = Math.max(0, Math.min((data.duration_seconds || 30) * 0.35, (data.duration_seconds || 30) - 32));
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      const src = ac.createMediaElementSource(audio);
      const analyser = ac.createAnalyser(); analyser.fftSize = 512;
      const timeAnalyser = ac.createAnalyser(); timeAnalyser.fftSize = 1024;
      const dest = ac.createMediaStreamDestination();
      src.connect(analyser); analyser.connect(dest); src.connect(timeAnalyser); src.connect(ac.destination);

      // 3. Combine canvas video + audio destination stream
      const videoStream = canvas.captureStream(30);
      const combined = new MediaStream([...videoStream.getVideoTracks(), ...dest.stream.getAudioTracks()]);
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
      const rec = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 3_500_000 });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      const done = new Promise(res => { rec.onstop = res; });

      // 4. Start audio + recording, draw for 30 seconds
      await audio.play();
      rec.start();
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const timeBuf = new Uint8Array(timeAnalyser.fftSize);
      // Scrolling waveform history
      const waveHistory = new Array(300).fill(128);
      const t0 = performance.now();
      const draw = () => {
        const elapsed = (performance.now() - t0) / 1000;
        analyser.getByteFrequencyData(buf);
        timeAnalyser.getByteTimeDomainData(timeBuf);
        // Push current amplitude peak into waveform history
        let peak = 0;
        for (let i = 0; i < timeBuf.length; i++) {
          const v = Math.abs(timeBuf[i] - 128);
          if (v > peak) peak = v;
        }
        waveHistory.push(peak);
        waveHistory.shift();

        // Background
        ctx.fillStyle = "#0B0E14"; ctx.fillRect(0, 0, 1080, 1920);

        // Scrolling waveform line (TikTok-style)
        const waveY = 480;
        const waveH = 220;
        const barW = 1080 / waveHistory.length;
        for (let i = 0; i < waveHistory.length; i++) {
          const v = waveHistory[i] / 128;
          const h = Math.min(waveH, v * waveH * 1.3);
          const x = i * barW;
          const grad = ctx.createLinearGradient(0, waveY - h / 2, 0, waveY + h / 2);
          grad.addColorStop(0, "#00F0FF");
          grad.addColorStop(0.5, "#FF8C00");
          grad.addColorStop(1, "#FF2A6D");
          ctx.fillStyle = grad;
          ctx.fillRect(x, waveY - h / 2, Math.max(2, barW - 1), h);
        }

        // Big radial spectrum
        const cx = 540, cy = 1050;
        const bars = 90;
        for (let i = 0; i < bars; i++) {
          const idx = Math.floor((i / bars) * buf.length * 0.6);
          const v = buf[idx] / 255;
          const angle = (i / bars) * Math.PI * 2 + elapsed * 0.15;
          const r0 = 250;
          const r1 = r0 + v * 320;
          ctx.strokeStyle = i < bars * 0.33 ? "#00F0FF" : i < bars * 0.66 ? "#FF8C00" : "#FF2A6D";
          ctx.lineWidth = 6;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(angle) * r0, cy + Math.sin(angle) * r0);
          ctx.lineTo(cx + Math.cos(angle) * r1, cy + Math.sin(angle) * r1);
          ctx.stroke();
        }
        // Center brand disc
        ctx.fillStyle = "#0B0E14"; ctx.beginPath(); ctx.arc(cx, cy, 230, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#00F0FF"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(cx, cy, 230, 0, Math.PI * 2); ctx.stroke();
        // Title text
        ctx.fillStyle = "#F8FAFC"; ctx.textAlign = "center";
        ctx.font = "900 60px 'Outfit', sans-serif";
        wrap(ctx, data.name || "Live Set", cx, cy - 20, 400, 70);
        ctx.font = "500 32px 'Plus Jakarta Sans', sans-serif";
        ctx.fillStyle = "#94A3B8";
        ctx.fillText(`by ${data.artist_name}`, cx, cy + 60);
        // Header brand
        ctx.textAlign = "left";
        ctx.font = "900 42px 'Outfit', sans-serif";
        ctx.fillStyle = "#F8FAFC";
        ctx.fillText("NEURAL", 60, 130);
        ctx.fillStyle = "#00F0FF";
        ctx.fillText("DECK", 60 + ctx.measureText("NEURAL").width, 130);
        // Tracklist overlay bottom
        ctx.fillStyle = "rgba(11,14,20,0.85)";
        ctx.fillRect(0, 1400, 1080, 520);
        ctx.fillStyle = "#00F0FF";
        ctx.font = "600 28px 'JetBrains Mono', monospace";
        ctx.fillText("// TRACKLIST", 60, 1460);
        ctx.font = "700 34px 'Plus Jakarta Sans', sans-serif";
        ctx.fillStyle = "#F8FAFC";
        const tl = (data.tracklist || []).slice(0, 6);
        tl.forEach((t, i) => {
          ctx.fillStyle = "#00F0FF";
          ctx.fillText(fmt(t.played_at), 60, 1520 + i * 60);
          ctx.fillStyle = "#F8FAFC";
          const line = `${t.title || "—"} · ${t.artist || ""}`;
          ctx.fillText(line.length > 40 ? line.slice(0, 37) + "…" : line, 200, 1520 + i * 60);
        });
        // Countdown badge
        const remaining = Math.max(0, 30 - Math.floor(elapsed));
        ctx.fillStyle = "#FF2A6D"; ctx.textAlign = "right";
        ctx.font = "900 40px 'JetBrains Mono', monospace";
        ctx.fillText(`0:${String(remaining).padStart(2, "0")}`, 1020, 130);

        if (elapsed < 30) requestAnimationFrame(draw);
        else { rec.stop(); audio.pause(); try { ac.close(); } catch {} }
      };
      requestAnimationFrame(draw);

      await done;
      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `neuraldeck-snippet-${id}.webm`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Snippet downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Snippet generation failed.");
    } finally { setSnippetBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center p-6">
        <div className="text-center space-y-4" data-testid="set-not-found">
          <div className="font-display font-black text-3xl">Set not found</div>
          <div className="text-[#94A3B8]">{error}</div>
          <Link to="/" className="text-[#00F0FF] font-black uppercase tracking-widest">Back to NeuralDeck</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="public-set-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Radio className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span></div>
        </Link>
        <div className="text-xs font-mono text-[#94A3B8] uppercase tracking-widest">Published Set</div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// {data.play_count} plays</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl leading-tight" data-testid="set-name">{data.name}</h1>
          <div className="text-[#94A3B8] mt-2">
            Mixed by {data.artist_user_id ? (
              <Link to={`/u/${data.artist_user_id}`} data-testid="set-artist-link" className="text-white font-bold hover:text-[#00F0FF] transition-colors">{data.artist_name}</Link>
            ) : <span className="text-white font-bold">{data.artist_name}</span>}
            {" · "}{fmt(data.duration_seconds)} · {new Date(data.created_at).toLocaleString()}
          </div>
        </div>

        <div className="rounded-2xl border border-[#2A354B] bg-[#121620] p-6 space-y-4">
          <div className="flex items-center gap-4">
            <button data-testid="set-play-button" onClick={toggle} className="w-16 h-16 rounded-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black flex items-center justify-center shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-colors">
              {playing ? <Pause className="w-6 h-6"/> : <Play className="w-6 h-6 ml-1"/>}
            </button>
            <div className="flex-1">
              <div className="h-2 bg-[#0B0E14] rounded-full border border-[#1E2638] overflow-hidden relative">
                <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#FF2A6D] transition-all" style={{ width: `${progress}%` }}/>
                {/* Comment timestamp markers */}
                {(data.duration_seconds || 0) > 0 && comments.map(c => (
                  <button key={c.id} data-testid={`comment-marker-${c.id}`} title={c.content}
                    onClick={() => seekTo(c.at_seconds)}
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#FFC107] hover:scale-150 transition-transform"
                    style={{ left: `${(c.at_seconds / data.duration_seconds) * 100}%` }}/>
                ))}
              </div>
              <div className="mt-2 flex justify-between font-mono text-xs text-[#94A3B8]">
                <span>{fmt((progress / 100) * (data.duration_seconds || 0))} / {fmt(data.duration_seconds)}</span>
                <button data-testid="generate-snippet-button" onClick={generateSnippet} disabled={snippetBusy}
                  className="text-[#00F0FF] hover:text-[#33F3FF] font-bold uppercase tracking-widest flex items-center gap-1">
                  {snippetBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Download className="w-3 h-3"/>}
                  {snippetBusy ? "Rendering 30s…" : "Snippet for TikTok"}
                </button>
              </div>
            </div>
          </div>
          <audio ref={audioRef} src={audioUrl} crossOrigin="anonymous" preload="metadata"/>
        </div>

        {/* Reactions */}
        <div className="flex items-center gap-3" data-testid="reactions-bar">
          {[
            { kind: "fire", Icon: Flame, color: "#FF8C00" },
            { kind: "heart", Icon: Heart, color: "#FF2A6D" },
          ].map(({ kind, Icon, color }) => {
            const active = reactions.my_reactions.includes(kind);
            return (
              <button key={kind} data-testid={`react-${kind}`} onClick={() => toggleReaction(kind)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all active:scale-95"
                style={{
                  background: active ? color : "transparent",
                  color: active ? "#000" : color,
                  borderColor: color,
                  boxShadow: active ? `0 0 20px ${color}66` : "none",
                }}>
                <Icon className="w-4 h-4" fill={active ? "currentColor" : "none"}/>
                <span className="font-mono font-bold">{reactions.counts[kind] || 0}</span>
              </button>
            );
          })}
        </div>

        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// Tracklist ({(data.tracklist || []).length})</div>
          <div className="space-y-1">
            {(data.tracklist || []).map((t, i) => (
              <div key={i} className="flex items-baseline gap-3 p-3 rounded-lg border border-[#1E2638] bg-[#121620]" data-testid={`tracklist-row-${i}`}>
                <span className="font-mono text-xs text-[#94A3B8] w-14">{fmt(t.played_at)}</span>
                <span className="font-mono text-xs text-[#00F0FF] w-8">[{t.deck_id}]</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{t.title}</div>
                  <div className="text-xs text-[#94A3B8] truncate">{t.artist}{t.bpm ? ` · ${t.bpm} BPM` : ""}{t.key ? ` · ${t.key}` : ""}</div>
                </div>
              </div>
            ))}
            {(!data.tracklist || data.tracklist.length === 0) && <div className="text-sm text-[#94A3B8]">No tracklist recorded.</div>}
          </div>
        </div>

        {/* Comments */}
        <div data-testid="comments-section">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-3 flex items-center gap-2">
            <MessageCircle className="w-3 h-3"/> Comments ({comments.length})
          </div>
          <form onSubmit={postComment} className="flex gap-2 mb-4">
            <Input data-testid="comment-input" value={newComment} onChange={e => setNewComment(e.target.value)}
              placeholder={user ? `Drop a comment at ${fmt(audioRef.current?.currentTime || 0)}…` : "Log in to comment"}
              disabled={!user}
              maxLength={500}
              className="bg-[#0B0E14] border-[#2A354B] text-white"/>
            <Button data-testid="comment-submit" type="submit" disabled={posting || !user || !newComment.trim()}
              className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
              {posting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            </Button>
          </form>
          <div className="space-y-2">
            {comments.map(c => (
              <div key={c.id} data-testid={`comment-${c.id}`} className="p-3 rounded-lg border border-[#1E2638] bg-[#121620]">
                <div className="flex items-baseline gap-2 mb-1">
                  <button onClick={() => seekTo(c.at_seconds)} className="font-mono text-xs text-[#FFC107] hover:text-white">{fmt(c.at_seconds)}</button>
                  <span className="font-bold text-sm">{c.user_name}</span>
                  <span className="text-[10px] text-[#94A3B8] font-mono ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-[#F8FAFC]">{c.content}</div>
              </div>
            ))}
            {comments.length === 0 && <div className="text-sm text-[#94A3B8]">Be the first to comment.</div>}
          </div>
        </div>

        <div className="text-center text-xs text-[#94A3B8] font-mono pt-8">
          Built on <Link to="/" className="text-[#00F0FF] hover:underline">NeuralDeck</Link>
        </div>
      </main>
    </div>
  );
}

function wrap(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  const lines = [];
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((l, i) => ctx.fillText(l, x, startY + i * lineHeight));
}
